import React, { useState, useEffect, useCallback } from "react";
import PagesHeader from "../../components/PageHeader";
import InstallerDatatable from "../../datatables/TrustedDealer/InstallerDatatable";
import ButtonBar from "../../components/ButtonNavigation/index";
import SubscriptionSummaryBanner from "../../components/InstallerSubscription/SubscriptionSummaryBanner";
import SeatGatekeeperModal from "../../components/InstallerSubscription/SeatGatekeeperModal";
import * as SubscriptionService from "../../services/InstallerSubscriptionService";
import * as InstallerData from "../../services/InstallerData";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Row, Col } from "antd";
import { SettingOutlined, UserAddOutlined, TeamOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";

const AllInstallers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loadingSeats, setLoadingSeats] = useState(true);
  const [seatData, setSeatData] = useState(null);
  const [gatekeeperType, setGatekeeperType] = useState(null); // 'not_subscribed' | 'limit_reached' | null
  const [showGatekeeperModal, setShowGatekeeperModal] = useState(false);

  const fetchSeatStatus = useCallback(async () => {
    setLoadingSeats(true);
    try {
      const installersRes = await dispatch(InstallerData.getInstallerListing());
      const usedCount = Array.isArray(installersRes) ? installersRes.length : 0;
      const res = await dispatch(SubscriptionService.getInstallerSeatStatus(usedCount));
      setSeatData(res);
    } catch (err) {
      console.error("Error fetching seat status", err);
    } finally {
      setLoadingSeats(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchSeatStatus();

    // Check for Stripe redirect success
    const paymentStatus = searchParams.get("payment_status");
    const sessionId = searchParams.get("session_id");
    if (paymentStatus === "success" || sessionId) {
      Swal.fire({
        icon: "success",
        title: "Payment Successful!",
        text: "Your Installer Pro Access subscription has been activated successfully.",
        confirmButtonColor: "#0284c7",
      });
      // Clear query params from URL
      searchParams.delete("payment_status");
      searchParams.delete("session_id");
      setSearchParams(searchParams);
    }
  }, [fetchSeatStatus, searchParams, setSearchParams]);

  const handleAddInstallerClick = () => {
    // If seat data is still loading, allow navigation directly to AddInstaller (which also validates)
    if (loadingSeats) {
      navigate("/add-installer");
      return;
    }

    const totalSeats = Number(seatData?.seats?.total_seats ?? 0);
    const usedSeats = Number(seatData?.seats?.used_seats ?? 0);
    const remainingSeats = seatData?.seats?.remaining_seats !== undefined
      ? Number(seatData.seats.remaining_seats)
      : Math.max(0, totalSeats - usedSeats);

    const isSubscribed = Boolean(seatData?.is_subscribed || totalSeats > 0);

    if (isSubscribed && remainingSeats <= 0) {
      setGatekeeperType("limit_reached");
      setShowGatekeeperModal(true);
      return;
    }

    if (!isSubscribed) {
      setGatekeeperType("not_subscribed");
      setShowGatekeeperModal(true);
      return;
    }

    navigate("/add-installer");
  };

  return (
    <div className="inspectionWrapper">
      <div className="inspectionHeader">
        <PagesHeader title="Installers Listing" />
      </div>

      <div className="buttonSection">
        <div className="container-xxl" style={{ paddingLeft: "32px", paddingRight: "32px" }}>
          <Row>
            <Col xs={24} md={24} className="text-end">
              <ButtonBar />
            </Col>
          </Row>
        </div>
      </div>

      <div className="inspectionWrapInner" style={{ padding: "20px 0 40px" }}>
        <div className="container-xxl" style={{ paddingLeft: "32px", paddingRight: "32px" }}>
          {/* Top Section Header with Title & Action Buttons */}
          <div className="installers-hero-header">
            <div className="hero-title-group">
              <h2 className="main-title">
                <TeamOutlined style={{ color: "#0284c7", fontSize: 22 }} />
                Installers
                <span className="title-tag">Team & Seats</span>
              </h2>
              <p className="main-subtitle">
                Manage your certified installer team and subscription capacity.
              </p>
            </div>

            <div className="hero-actions-group">
              <button
                type="button"
                className="btn-manage-sub"
                onClick={() => navigate("/installer/manage-subscription")}
              >
                <span className="btn-icon-box">
                  <SettingOutlined />
                </span>
                Manage Subscription
              </button>

              <button
                type="button"
                className="btn-add-installer-premium"
                onClick={handleAddInstallerClick}
              >
                <UserAddOutlined style={{ fontSize: 16 }} />
                Add Installer
              </button>
            </div>
          </div>

          {/* Top Subscription Summary Banner */}
          <SubscriptionSummaryBanner seatData={seatData} loading={loadingSeats} />

          {/* Installers Listing Datatable */}
          <InstallerDatatable />
        </div>
      </div>

      {/* Luxury Seat Gatekeeper Modal */}
      <SeatGatekeeperModal
        visible={showGatekeeperModal}
        onClose={() => setShowGatekeeperModal(false)}
        type={gatekeeperType}
        seatData={seatData}
      />
    </div>
  );
};

export default AllInstallers;
